import { useEffect, useState, type MouseEvent } from 'react';
import { IconButton, Menu, MenuItem, ListItemText, CircularProgress, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useDataProvider, useNotify } from 'react-admin';
import type { AnalyzePrintingsRow } from './types';

interface FailureCauseOption {
  id: number;
  name: string;
}

interface Props {
  printing: AnalyzePrintingsRow;
  onUpdated: (next: { id: number; name: string } | null) => void;
}

export const FailureCausePicker = ({ printing, onUpdated }: Props) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [options, setOptions] = useState<FailureCauseOption[] | null>(null);
  const [saving, setSaving] = useState(false);

  // Lazy-load the failure causes list the first time the picker opens.
  useEffect(() => {
    if (!anchor || options !== null) return;
    let cancelled = false;
    dataProvider
      .getList<FailureCauseOption>('failure-causes', {
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'name', order: 'ASC' },
        filter: {},
      })
      .then(({ data }) => {
        if (!cancelled) setOptions(data);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.error(err);
        notify(`Could not load failure causes: ${err.message}`, { type: 'error' });
        setOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [anchor, options, dataProvider, notify]);

  const open = (e: MouseEvent<HTMLElement>) => setAnchor(e.currentTarget);
  const close = () => setAnchor(null);

  const apply = async (next: FailureCauseOption | null) => {
    setSaving(true);
    try {
      await dataProvider.update('printings', {
        id: printing.id,
        data: { failureCauseId: next?.id ?? null },
        previousData: printing,
      });
      onUpdated(next);
      notify(next ? `Failure cause set to "${next.name}"` : 'Failure cause cleared', {
        type: 'info',
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      const message = err instanceof Error ? err.message : 'Unknown error';
      notify(`Failed to update failure cause: ${message}`, { type: 'error' });
    } finally {
      setSaving(false);
      close();
    }
  };

  const currentLabel = printing.failureCause?.name ?? '—';

  return (
    <>
      <span>{currentLabel}</span>
      <Tooltip title="Edit failure cause">
        <span>
          <IconButton size="small" onClick={open} disabled={saving} aria-label="Edit failure cause">
            {saving ? <CircularProgress size={16} /> : <EditIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={close}>
        {options === null && (
          <MenuItem disabled>
            <CircularProgress size={16} />
          </MenuItem>
        )}
        {options !== null && options.length === 0 && (
          <MenuItem disabled>No failure causes available</MenuItem>
        )}
        {options !== null && options.length > 0 && (
          <MenuItem onClick={() => apply(null)} selected={printing.failureCause == null}>
            <ListItemText primary={<em>None</em>} />
          </MenuItem>
        )}
        {options?.map((cause) => (
          <MenuItem
            key={cause.id}
            onClick={() => apply(cause)}
            selected={printing.failureCause?.id === cause.id}
          >
            <ListItemText primary={cause.name} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
